import { useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const COLORS = {
  palette: {
    primary: {
      GREEN50: '#f2f5e6',
      GREEN100: '#d5dfb1',
      GREEN200: '#c1d08c',
      GREEN300: '#a5bb57',
      GREEN400: '#93ad36',
      GREEN500: '#789904',
      VIOLET50: '#e8e9ed',
      VIOLET100: '#b9b9c8',
      VIOLET200: '#9798ad',
      VIOLET300: '#676887',
      VIOLET400: '#494b70',
      VIOLET500: '#1c1e4c',
    },
    secondary: {
      BROWN50: '#f8f7f6',
      BROWN75: '#e1ddda',
      BROWN100: '#d4cfcb',
      BROWN200: '#c2bbb4',
      BROWN300: '#b5ada5',
    },
    success: {
      SUCCESS50: '#F0FDF4',
      SUCCESS100: '#DCFCE7',
      SUCCESS200: '#BBF7D0',
      SUCCESS300: '#86EFAC',
      SUCCESS400: '#4ADE80',
      SUCCESS500: '#22C55E',
      SUCCESS600: '#16A34A',
      SUCCESS700: '#15803D',
      SUCCESS800: '#166534',
      SUCCESS900: '#14532D',
    },
    warning: {
      ORNAGE50: '#FFFBEB',
      ORNAGE100: '#FEF3C7',
      ORNAGE200: '#FDE68A',
      ORNAGE300: '#FBD34D',
      ORNAGE400: '#FBBF24',
      ORNAGE500: '#F59E0B',
      ORNAGE600: '#D97706',
      ORNAGE700: '#B45309',
      ORNAGE800: '#92400E',
      ORNAGE900: '#78350F',
    },
    error: {
      RED50: '#FEF2F2',
      RED100: '#FEE2E2',
      RED200: '#FECACA',
      RED300: '#FCA5A5',
      RED400: '#F87171',
      RED500: '#EF4444',
      RED600: '#DC2626',
      RED700: '#B91C1C',
      RED800: '#991B1B',
      RED900: '#7F1D1D',
    },
    neutral: {
      GREY50: '#F9FAFB',
      GREY100: '#F3F4F6',
      GREY200: '#E5E7EB',
      GREY300: '#D1D5DB',
      GREY400: '#9CA3AF',
      GREY500: '#6B7280',
      GREY600: '#4B5563',
      GREY700: '#374151',
      GREY800: '#1F2937',
      GREY900: '#111827',
    },
    black: {
      BLACK100: '#cccccc',
      BLACK200: '#999999',
      BLACK300: '#666666',
      BLACK400: '#333333',
      BLACK500: '#000000',
      BLACK600: '#000000',
      BLACK700: '#000000',
      BLACK800: '#000000',
      BLACK900: '#000000',
    },
    white: {
      WHITE100: '#ffffff',
      WHITE200: '#ffffff',
      WHITE300: '#ffffff',
      WHITE400: '#ffffff',
      WHITE500: '#ffffff',
      WHITE600: '#cccccc',
      WHITE700: '#999999',
      WHITE800: '#666666',
      WHITE900: '#333333',
    },
    indigo: {
      INDIGO100: '#ccdfff',
      INDIGO200: '#99bfff',
      INDIGO300: '#66a0ff',
      INDIGO400: '#3380ff',
      INDIGO500: '#0060ff',
      INDIGO600: '#004dcc',
      INDIGO700: '#003a99',
      INDIGO800: '#002666',
      INDIGO900: '#001333',
    },
  },
};

const Theme = ({ children, language }) => {
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            50: COLORS.palette.primary.GREEN50,
            100: COLORS.palette.primary.GREEN100,
            200: COLORS.palette.primary.GREEN200,
            300: COLORS.palette.primary.GREEN300,
            400: COLORS.palette.primary.GREEN400,
            main: COLORS.palette.primary.GREEN500,
          },
          secondary: {
            50: COLORS.palette.primary.VIOLET50,
            100: COLORS.palette.primary.VIOLET100,
            200: COLORS.palette.primary.VIOLET200,
            300: COLORS.palette.primary.VIOLET300,
            400: COLORS.palette.primary.VIOLET400,
            main: COLORS.palette.primary.VIOLET500,
          },
          success: {
            50: COLORS.palette.success.SUCCESS50,
            100: COLORS.palette.success.SUCCESS100,
            200: COLORS.palette.success.SUCCESS200,
            300: COLORS.palette.success.SUCCESS300,
            400: COLORS.palette.success.SUCCESS400,
            main: COLORS.palette.success.SUCCESS500,
            600: COLORS.palette.success.SUCCESS600,
            700: COLORS.palette.success.SUCCESS700,
            800: COLORS.palette.success.SUCCESS800,
            900: COLORS.palette.success.SUCCESS900,
          },
          warning: {
            50: COLORS.palette.warning.ORNAGE50,
            100: COLORS.palette.warning.ORNAGE100,
            200: COLORS.palette.warning.ORNAGE200,
            300: COLORS.palette.warning.ORNAGE300,
            400: COLORS.palette.warning.ORNAGE400,
            main: COLORS.palette.warning.ORNAGE500,
            600: COLORS.palette.warning.ORNAGE600,
            700: COLORS.palette.warning.ORNAGE700,
            800: COLORS.palette.warning.ORNAGE800,
            900: COLORS.palette.warning.ORNAGE900,
          },
          error: {
            50: COLORS.palette.error.RED50,
            100: COLORS.palette.error.RED100,
            200: COLORS.palette.error.RED200,
            300: COLORS.palette.error.RED300,
            400: COLORS.palette.error.RED400,
            main: COLORS.palette.error.RED500,
            600: COLORS.palette.error.RED600,
            700: COLORS.palette.error.RED700,
            800: COLORS.palette.error.RED800,
            900: COLORS.palette.error.RED900,
          },
          grey: {
            50: COLORS.palette.neutral.GREY50,
            100: COLORS.palette.neutral.GREY100,
            200: COLORS.palette.neutral.GREY200,
            300: COLORS.palette.neutral.GREY300,
            400: COLORS.palette.neutral.GREY400,
            main: COLORS.palette.neutral.GREY500,
            600: COLORS.palette.neutral.GREY600,
            700: COLORS.palette.neutral.GREY700,
            800: COLORS.palette.neutral.GREY800,
            900: COLORS.palette.neutral.GREY900,
          },
          black: {
            100: COLORS.palette.black.BLACK100,
            200: COLORS.palette.black.BLACK200,
            300: COLORS.palette.black.BLACK300,
            400: COLORS.palette.black.BLACK400,
            main: COLORS.palette.black.BLACK500,
            600: COLORS.palette.black.BLACK600,
            700: COLORS.palette.black.BLACK700,
            800: COLORS.palette.black.BLACK800,
            900: COLORS.palette.black.BLACK900,
          },
          white: {
            100: COLORS.palette.white.WHITE100,
            200: COLORS.palette.white.WHITE200,
            300: COLORS.palette.white.WHITE300,
            400: COLORS.palette.white.WHITE400,
            main: COLORS.palette.white.WHITE500,
            600: COLORS.palette.white.WHITE600,
            700: COLORS.palette.white.WHITE700,
            800: COLORS.palette.white.WHITE800,
            900: COLORS.palette.white.WHITE900,
          },
          brown: {
            main: COLORS.palette.secondary.BROWN50,
            50: COLORS.palette.secondary.BROWN50,
            75: COLORS.palette.secondary.BROWN75,
            100: COLORS.palette.secondary.BROWN100,
            200: COLORS.palette.secondary.BROWN200,
            300: COLORS.palette.secondary.BROWN300,
          },
        },
        typography: {
          fontFamily: language === 'ar' ? 'CalibriArabic' : 'Inter',
          color: COLORS.palette.black.BLACK500,
          fontSize: 16,
          h1: {
            fontSize: 96,
            letterSpacing: '2%',
            lineHeight: '112px',
          },
          h2: {
            fontSize: 60,
            letterSpacing: '2%',
            lineHeight: '72px',
          },
          h3: {
            fontSize: 48,
            letterSpacing: '2%',
            lineHeight: '56px',
          },
          h4: {
            fontSize: 34,
            letterSpacing: '2%',
            lineHeight: '42px',
          },
          h5: {
            fontSize: 24,
            letterSpacing: '2%',
            lineHeight: '32px',
          },
          h6: {
            fontSize: 20,
            letterSpacing: '0%',
            lineHeight: '32px',
          },
          subtitle1: {
            fontSize: 16,
            letterSpacing: '0%',
            lineHeight: '28px',
          },
          subtitle2: {
            fontSize: 14,
            letterSpacing: '0%',
            lineHeight: '22px',
          },
          body1: {
            fontSize: 16,
            letterSpacing: '0%',
            lineHeight: '24px',
          },
          body2: {
            fontSize: 14,
            letterSpacing: '0%',
            lineHeight: '20px',
          },
          caption: {
            fontSize: 12,
            letterSpacing: '0%',
            lineHeight: '20px',
          },
          overline: {
            fontSize: 12,
            letterSpacing: '0%',
            lineHeight: '32px',
          },
          button: {
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: 600,
            letterSpacing: '0%',
            lineHeight: '16px',
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '5px',
                padding: '12px 16px',
                textTransform: 'capitalize',
                '&.Mui-disabled': {
                  color: COLORS.palette.neutral.GREY50,
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                color: COLORS.palette.black.BLACK500,
                backgroundColor: COLORS.palette.primary.VIOLET50,
                borderRadius: '3px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderWidth: '0px',
                },
                '.MuiInputBase-root.Mui-focused': {
                  borderColor: COLORS.palette.black.BLACK500,
                },
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                color: COLORS.palette.black.BLACK500,
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
              },
            },
          },
          MuiGrid: {
            styleOverrides: {
              root: {
                '.MuiFormLabel-root.Mui-focused': {
                  color: COLORS.palette.black.BLACK500,
                },
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                marginLeft: 0,
                color: COLORS.palette.error.RED500,
                textAlign: 'inherit',
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                fontSize: '16px',
                cursor: 'pointer',
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: COLORS.palette.neutral.GREY200,
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                borderRadius: '9px',
                '&.Mui-selected': {
                  backgroundColor: COLORS.palette.primary.VIOLET50,
                  color: COLORS.palette.indigo.INDIGO500,
                  '.MuiListItemIcon-root': {
                    color: COLORS.palette.indigo.INDIGO500,
                  },
                  ':hover': {
                    backgroundColor: COLORS.palette.primary.VIOLET50,
                  },
                },
                '&.Mui-focusVisible': {
                  backgroundColor: COLORS.palette.primary.VIOLET50,
                },
                ':hover': {
                  backgroundColor: COLORS.palette.primary.VIOLET50,
                },
              },
            },
          },
          MuiTypography: {
            styleOverrides: {
              root: {
                textAlign: 'start',
              },
            },
          },
          MuiDialog: {
            defaultProps: {
              onClick: e => e.stopPropagation(),
              BackdropProps: {
                sx: {
                  pointerEvents: 'none',
                },
              },
            },
          },
        },
      }),
    [language],
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default Theme;
